import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
} from "@mui/material";
import {
  PlayArrow as SendIcon,
  AutoFixHigh as MockIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  Code as CodeIcon,
  Refresh as ResetIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import type { EndpointRequest, HttpMethod } from "../types";
import { ModernMethodChip } from "./ModernUiComponents";
import { JsonEditorInput } from "./JsonEditorInput";
import apiService from "../../../service/Api";

interface ApiTesterSandboxProps {
  request: EndpointRequest;
}

export const ApiTesterSandbox: React.FC<ApiTesterSandboxProps> = ({ request }) => {
  const defaultUrl = request.endpoint || `/api/v1/${request.component.toLowerCase().replace(/\s+/g, "-")}`;
  const [method, setMethod] = useState<HttpMethod>((request.method as HttpMethod) || "GET");
  const [url, setUrl] = useState<string>(defaultUrl);
  const [payloadText, setPayloadText] = useState<string>(request.payload || "");
  const [headersText, setHeadersText] = useState<string>(
    JSON.stringify({ "Content-Type": "application/json", Authorization: "Bearer [AUTH_TOKEN]" }, null, 2)
  );

  const [loading, setLoading] = useState(false);
  const [responseStatus, setResponseStatus] = useState<number | null>(null);
  const [responseTime, setResponseTime] = useState<number | null>(null);
  const [responseBody, setResponseBody] = useState<string | null>(null);
  const [isMock, setIsMock] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleSendLiveRequest = async () => {
    setLoading(true);
    setResponseStatus(null);
    setResponseBody(null);
    setIsMock(false);

    const startTime = performance.now();
    try {
      let parsedPayload: Record<string, unknown> | undefined = undefined;
      if (payloadText && payloadText.trim()) {
        try {
          parsedPayload = JSON.parse(payloadText) as Record<string, unknown>;
        } catch {
          parsedPayload = undefined;
        }
      }

      let res;
      if (method === "GET") {
        res = await apiService.get(url);
      } else if (method === "POST") {
        res = await apiService.post(url, parsedPayload);
      } else if (method === "PUT") {
        res = await apiService.put(url, parsedPayload);
      } else if (method === "PATCH") {
        res = await apiService.patch(url, parsedPayload);
      } else if (method === "DELETE") {
        res = await apiService.delete(url);
      }

      const endTime = performance.now();
      setResponseTime(Math.round(endTime - startTime));
      setResponseStatus(res?.status || 200);
      setResponseBody(JSON.stringify(res?.data || { status: "success" }, null, 2));
      toast.success(`Request succeeded with status ${res?.status || 200}`);
    } catch (err: unknown) {
      const endTime = performance.now();
      setResponseTime(Math.round(endTime - startTime));

      const apiErr = err as { response?: { status?: number; data?: unknown }; message?: string };
      setResponseStatus(apiErr.response?.status || 500);
      setResponseBody(JSON.stringify(apiErr.response?.data || { error: apiErr.message || "Failed to fetch" }, null, 2));
      toast.error(`Request failed with status ${apiErr.response?.status || 500}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSimulateMock = () => {
    setLoading(true);
    setIsMock(true);

    setTimeout(() => {
      let mockData: Record<string, unknown> = {
        success: true,
        message: `Mock ${method} response for ${request.component}`,
        timestamp: new Date().toISOString(),
      };

      if (payloadText && payloadText.trim()) {
        try {
          const parsed = JSON.parse(payloadText);
          if (typeof parsed === "object" && parsed !== null) {
            mockData = {
              ...mockData,
              received_data: parsed,
              generated_id: Math.floor(Math.random() * 9000) + 1000,
            };
          }
        } catch {
          // ignore
        }
      }

      setResponseTime(Math.floor(Math.random() * 40) + 15);
      setResponseStatus(method === "POST" ? 201 : 200);
      setResponseBody(JSON.stringify(mockData, null, 2));
      setLoading(false);
      toast.info("Simulated mock API response!");
    }, 400);
  };

  const handleCopyResponse = () => {
    if (!responseBody) return;
    navigator.clipboard.writeText(responseBody);
    setCopied(true);
    toast.success("Response copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Box sx={{ width: "100%" }}>
      {/* Sandbox Header / URL Bar */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2.5,
          borderRadius: "16px",
          border: "1px solid rgba(226, 232, 240, 0.9)",
          backgroundColor: "#F8FAFC",
        }}
      >
        <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
          <FormControl size="small" sx={{ minWidth: 110, "& .MuiOutlinedInput-root": { borderRadius: "10px", bg: "white" } }}>
            <Select value={method} onChange={(e) => setMethod(e.target.value as HttpMethod)}>
              <MenuItem value="GET">GET</MenuItem>
              <MenuItem value="POST">POST</MenuItem>
              <MenuItem value="PUT">PUT</MenuItem>
              <MenuItem value="PATCH">PATCH</MenuItem>
              <MenuItem value="DELETE">DELETE</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            size="small"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="/api/v1/resource/"
            sx={{ flex: 1, minWidth: 260, "& .MuiOutlinedInput-root": { borderRadius: "10px", backgroundColor: "white" } }}
          />

          <Button
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
            onClick={handleSendLiveRequest}
            sx={{ borderRadius: "10px", fontWeight: 700, textTransform: "none", px: 2.5 }}
          >
            Send Request
          </Button>

          <Button
            variant="outlined"
            disabled={loading}
            startIcon={<MockIcon />}
            onClick={handleSimulateMock}
            sx={{ borderRadius: "10px", fontWeight: 700, textTransform: "none", px: 2.5 }}
          >
            Simulate Mock
          </Button>
        </Box>
      </Paper>

      {/* Payload and Headers Input */}
      <Box sx={{ mb: 2.5 }}>
        <JsonEditorInput
          value={payloadText}
          onChange={(val) => setPayloadText(val)}
          label="Request Body (JSON)"
          minRows={4}
          maxRows={8}
        />
      </Box>

      {/* Response Panel */}
      {responseStatus !== null && (
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            borderRadius: "16px",
            border: "1px solid rgba(226, 232, 240, 0.9)",
            backgroundColor: "#0F172A",
            color: "#F8FAFC",
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Chip
                label={`${responseStatus} ${responseStatus < 300 ? "OK" : "ERROR"}`}
                color={responseStatus < 300 ? "success" : "error"}
                size="small"
                sx={{ fontWeight: 800 }}
              />
              {responseTime !== null && (
                <Typography variant="caption" sx={{ color: "#94A3B8", fontWeight: 600 }}>
                  Time: {responseTime} ms
                </Typography>
              )}
              {isMock && (
                <Chip label="Mock Response" size="small" variant="outlined" sx={{ color: "#38BDF8", borderColor: "#38BDF8", height: 20 }} />
              )}
            </Box>

            <Tooltip title="Copy Response">
              <IconButton size="small" onClick={handleCopyResponse} sx={{ color: "#94A3B8" }}>
                {copied ? <CheckIcon fontSize="small" color="success" /> : <CopyIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Box>

          <Box
            sx={{
              fontFamily: "'Fira Code', 'Consolas', monospace",
              fontSize: "0.825rem",
              maxHeight: 300,
              overflowY: "auto",
              color: "#38BDF8",
            }}
          >
            <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>{responseBody}</pre>
          </Box>
        </Paper>
      )}
    </Box>
  );
};
